// @ts-check

import { mutationOptions, queryOptions } from "@tanstack/react-query"
import { getChatSessionImages } from "../api"
import { ChatSessionQueryKeys } from "../sessions"
import axios from "axios"

const ChatImagesQueryKeys = {
  /**
   * @param {string} sessionId
   * @param {string} [projectId]
   */
  CHAT_SESSION_IMAGES: (sessionId, projectId) => [
    ...ChatSessionQueryKeys.CHAT_SESSION(sessionId), "images"
  ],

  /**
   * @param {string} sessionId
   * @param {string} [projectId]
   */
  CHAT_SESSION_THUMBNAIL_IMAGE: (sessionId, projectId) => [
    ...ChatSessionQueryKeys.CHAT_SESSION_LIST(projectId), "thumbnails", sessionId
  ],
}

const ChatImagesQueryOptions = {
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  CHAT_SESSION_IMAGES: ({ sessionId, projectId, userToken }) => queryOptions({
    queryKey: ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId, projectId),
    queryFn: ({ signal, client }) => (
      getChatSessionImages(sessionId, userToken, { signal })
        .then(async (data) => {
          if(data.images.length > 0) {
            await client.cancelQueries({
              queryKey: ChatImagesQueryKeys.CHAT_SESSION_THUMBNAIL_IMAGE(sessionId, projectId)
            })

            client.setQueryData(
              ChatImagesQueryKeys.CHAT_SESSION_THUMBNAIL_IMAGE(sessionId, projectId),
              data.images[data.images.length - 1],
            )
          }

          return data.images
        })
    ),
    staleTime: Infinity,
    enabled: userToken != null && sessionId != null,
  }),

  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   * @param {string} [params.projectId]
   */
  CHAT_SESSION_THUMBNAIL_IMAGE: ({ sessionId, projectId }) => queryOptions({
    queryKey: ChatImagesQueryKeys.CHAT_SESSION_THUMBNAIL_IMAGE(sessionId, projectId),
    queryFn: ({ signal }) => (
      Promise.resolve(/** @type {ChatImage} */(null))
    ),
    staleTime: Infinity,
    enabled: false,
  }),
}

/**
 * @param {File} file
 */
async function uploadImage(file) {
  const formData = new FormData()
  formData.append("file", file)
  const uploadEndpoint = new URL("upload/images", import.meta.env.VITE_SOCKET_URL).href

  const { data, status } = await axios.post(uploadEndpoint, formData)
  if (status !== 200) throw new Error("Error uploading image")

  /** @type {ChatImage} */
  const image = data.data.image
  return image
}

const ChatImagesMutationOptions = {
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} [params.projectId]
   */
  CHAT_SESSION_IMAGES: ({ sessionId, projectId }) => mutationOptions({
    /** @param {{file: File}} params */
    mutationFn: ({file}) => uploadImage(file),
    async onSuccess(data, variables, _, context) {
      await context.client.cancelQueries({
        queryKey: ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId, projectId)
      })

      context.client.setQueryData(
        ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId, projectId),
        /** @param {ChatImage[]} images */
        (images = []) => [...images, data],
      )
    },
  }),
}

export {
  ChatImagesQueryKeys,
  ChatImagesQueryOptions,
  ChatImagesMutationOptions,
}
